import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form, Button, Card, Container, Alert } from 'react-bootstrap';
import { authAPI } from '../../utils/api';
import { useTranslation } from '../../i18n';
import { translateBackendMessage } from '../../utils/translateMessage';
import styles from './Auth.module.css';

const validationSchema = Yup.object({
  password: Yup.string()
    .required('שדה חובה'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'הסיסמאות אינן תואמות')
    .required('שדה חובה')
});

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);
        await authAPI.resetPassword(token, values.password);
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err) {
        const rawMessage = err.response?.data?.message;
        const translated = translateBackendMessage(rawMessage);
        setError(translated || rawMessage || t('notifications.frontend.auth.resetPasswordError'));
      } finally {
        setLoading(false);
      }
    }
  });

  if (success) {
    return (
      <Container className={styles.authContainer}>
        <Card className={styles.authCard}>
          <Card.Body>
            <h2 className={styles.authTitle}>{t('ui.auth.resetPasswordTitle')}</h2>
            <Alert variant="success">
              {t('ui.auth.resetSuccess')}
            </Alert>
            <div className={styles.authLinks}>
              <Link to="/login">{t('ui.auth.resetAutoRedirect')}</Link>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className={styles.authContainer}>
      <Card className={styles.authCard}>
        <Card.Body>
          <h2 className={styles.authTitle}>{t('ui.auth.resetPasswordTitle')}</h2>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('ui.auth.newPassword')}</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.password && formik.errors.password}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.password}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>{t('ui.auth.confirmPassword')}</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 mb-4"
              disabled={loading}
            >
              {loading ? t('ui.auth.resetSubmitting') : t('ui.auth.resetButton')}
            </Button>

            <div className={styles.authLinks}>
              <Link to="/login">{t('ui.auth.backToLogin')}</Link>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ResetPassword; 